import { useEffect, useState } from "react";

import { FormDialog, TextField } from "@/components";

/**
 * 비밀번호 바꾸기.
 *
 * 바꾸면 **다른 기기의 로그인은 전부 끊긴다.** 비밀번호를 바꾸는 이유가 보통
 * "누가 알아버렸다"라서 그게 맞는데, 모르고 있으면 놀라므로 미리 적어둔다.
 */

const MIN_LENGTH = 8;

type PasswordDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending?: boolean;
  /** 서버가 거절한 이유. "지금 쓰는 비밀번호가 맞지 않습니다" 같은 것. */
  error?: string | null;
  onSubmit: (input: { current: string; next: string }) => void;
};

export function PasswordDialog({
  open,
  onOpenChange,
  pending = false,
  error,
  onSubmit,
}: PasswordDialogProps) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  // 비밀번호는 창을 닫을 때 반드시 지운다. 남겨두면 다음에 열었을 때 남의
  // 화면에 그대로 떠 있을 수 있다.
  useEffect(() => {
    if (!open) {
      setCurrent("");
      setNext("");
      setConfirm("");
    }
  }, [open]);

  const tooShort = next.length > 0 && next.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && next !== confirm;

  const hint = tooShort
    ? `${MIN_LENGTH}자 이상이어야 해요`
    : mismatch
      ? "새 비밀번호가 서로 달라요"
      : (error ?? null);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="비밀번호 바꾸기"
      description="바꾸면 다른 기기의 로그인은 모두 끊겨요."
      submitLabel="바꾸기"
      pendingLabel="바꾸는 중…"
      pending={pending}
      canSubmit={
        current.length > 0 && next.length >= MIN_LENGTH && next === confirm
      }
      hint={hint ? <p className="text-danger text-[13px]">{hint}</p> : undefined}
      onSubmit={() => onSubmit({ current, next })}
    >
      <div className="flex flex-col gap-2">
        <TextField
          label="지금 비밀번호"
          type="password"
          autoComplete="current-password"
          value={current}
          placeholder="지금 비밀번호"
          autoFocus
          onChange={(event) => setCurrent(event.target.value)}
        />
        <TextField
          label="새 비밀번호"
          type="password"
          autoComplete="new-password"
          value={next}
          placeholder={`새 비밀번호 (${MIN_LENGTH}자 이상)`}
          onChange={(event) => setNext(event.target.value)}
        />
        <TextField
          label="새 비밀번호 다시"
          type="password"
          autoComplete="new-password"
          value={confirm}
          placeholder="새 비밀번호 다시"
          onChange={(event) => setConfirm(event.target.value)}
        />
      </div>
    </FormDialog>
  );
}
